// Tips content is stored in-code (FR/EN) to keep it editable without touching i18n keys.
// Selection is based on sport + persona + goal keywords.

const normalize = (v) => String(v || '').trim().toLowerCase();

/**
 * @typedef {Object} Tip
 * @property {string} id
 * @property {'run'|'bike'|'any'} sport
 * @property {{en: string, fr: string}} title
 * @property {{en: string, fr: string}} body
 * @property {{ personas?: string[], keywords?: string[] }} [match]
 */

/** @type {Tip[]} */
export const TIPS = [
  // --- RUN (generic) ---
  {
    id: 'run_warmup',
    sport: 'run',
    title: { en: 'Warm-up: the easiest performance win', fr: 'Échauffement : le gain le plus simple' },
    body: {
      en: 'Before intervals, do 10–15 min easy + 3–4 short strides. It improves quality without adding fatigue.',
      fr: "Avant une séance dure : 10–15 min facile + 3–4 accélérations courtes. La qualité monte sans ajouter de fatigue."
    }
  },
  {
    id: 'run_easy_is_easy',
    sport: 'run',
    title: { en: 'Easy runs should feel easy', fr: 'Les footings doivent être faciles' },
    body: {
      en: 'Most progress comes from low intensity. If you can’t speak in full sentences, slow down.',
      fr: "La plupart des progrès viennent du faible effort. Si tu ne peux pas parler en phrases complètes, ralentis."
    }
  },
  {
    id: 'run_consistency',
    sport: 'run',
    title: { en: 'Consistency beats hero workouts', fr: "La régularité > les séances héroïques" },
    body: {
      en: '3 good weeks in a row beats 1 perfect week followed by 2 missed weeks. We optimize for sustainability.',
      fr: "3 bonnes semaines d’affilée valent mieux qu’une semaine parfaite puis 2 semaines ratées. On vise le durable."
    }
  },
  {
    id: 'run_trail_hills',
    sport: 'run',
    title: { en: 'Trail: manage effort, not pace', fr: 'Trail : gérer l’effort, pas l’allure' },
    body: {
      en: 'On climbs, walk early. Don’t wait until your heart rate explodes (Z3/Z4), or it will be hard to recover.',
      fr: "En montée, marchez tôt. N'attendez pas que votre cardio explose (Z3/Z4) pour ralentir, car il sera difficile de le faire redescendre ensuite."
    },
    match: { keywords: ['trail', 'montagne', 'technical', 'dénivel', 'elevation'] }
  },
  {
    id: 'run_trail_cramps',
    sport: 'run',
    title: { en: 'Trail: restart slowly after climbs', fr: 'Trail : relancez doucement après les côtes' },
    body: {
      en: 'At the top of a climb, jog slowly before accelerating. This helps muscles adapt and limits cramps.',
      fr: "Arrivé en haut d'une côte, relancez doucement en trottinant avant d'accélérer. Cela permet aux muscles de s'adapter et limite l'apparition de crampes."
    },
    match: { keywords: ['trail', 'montagne', 'technical', 'dénivel', 'elevation'] }
  },
  {
    id: 'run_downhill_technique',
    sport: 'run',
    title: { en: 'Downhill: stay light', fr: 'Descente : restez léger' },
    body: {
      en: 'Lean slightly forward and increase your cadence to reduce impact. Don’t brake too much with your heels.',
      fr: "Penchez-vous légèrement vers l'avant et augmentez la cadence pour réduire les chocs. Évitez de trop freiner avec les talons."
    },
    match: { keywords: ['trail', 'montagne', 'technical', 'dénivel', 'elevation', 'downhill'] }
  },
  {
    id: 'general_stretching',
    sport: 'run', // Applies to run mostly, but good for all
    title: { en: 'Stretch cold or later', fr: 'Étirements : à froid ou plus tard' },
    body: {
      en: 'Stretching is best 3-4h after effort or in the evening. Stretching immediately after intense effort can worsen micro-tears.',
      fr: "Les étirements sont plus efficaces à distance des séances (3-4h après ou le soir). S'étirer à chaud peut aggraver les micro-lésions."
    }
  },
  {
    id: 'run_frequency',
    sport: 'run',
    title: { en: 'Frequency > Intensity', fr: 'Régularité > Intensité' },
    body: {
      en: 'Better to run 3x 45min per week than one big 2.5h run. Consistency builds the base.',
      fr: "Mieux vaut courir 3 fois 45 minutes par semaine qu'une seule grosse sortie de 2h30. La régularité construit le foncier."
    }
  },
  {
    id: 'bike_cadence',
    sport: 'bike',
    title: { en: 'High cadence saves legs', fr: 'La vélocité sauve les jambes' },
    body: {
      en: 'Keep a high cadence (85-95 rpm). Grinding big gears uses more muscle glycogen and fatigues you faster than cardio.',
      fr: "Gardez une cadence élevée (85-95 tr/min). Tirer de gros braquets sollicite trop les muscles et épuise plus vite que le cardio."
    }
  },
  {
    id: 'bike_hydration',
    sport: 'bike',
    title: { en: 'Drink early and often', fr: 'Boire tôt et souvent' },
    body: {
      en: 'Sip water every 10-15 mins, even if it’s cool. Dehydration is the #1 enemy of performance on long rides.',
      fr: "Buvez par petites gorgées toutes les 10-15 min, même par temps frais. La déshydratation est l'ennemi n°1 sur les sorties longues."
    }
  },
  {
    id: 'bike_drafting',
    sport: 'bike',
    title: { en: 'Drafting is free speed', fr: 'L’abri, c’est de l’énergie gratuite' },
    body: {
      en: 'Riding in someone’s wheel saves up to 30% energy. Stay alert and keep hands near the brakes.',
      fr: "Rouler dans la roue permet d'économiser jusqu'à 30% d'énergie. Restez vigilant et gardez les mains proches des freins."
    }
  },
  {
    id: 'general_sleep',
    sport: 'any',
    title: { en: 'Sleep is your recovery tool', fr: 'Le sommeil est votre arme' },
    body: {
      en: 'Training breaks you down; sleep builds you back up. It’s when your body repairs muscle tissue.',
      fr: "L'entraînement vous fatigue, le sommeil vous reconstruit. C'est là que le corps répare les tissus musculaires."
    }
  },
  {
    id: 'general_nutrition_test',
    sport: 'any',
    title: { en: 'Nothing new on race day', fr: 'Rien de nouveau le jour J' },
    body: {
      en: 'Always test gels, bars, and drinks in training. Your stomach needs to practice digesting while working.',
      fr: "Testez toujours votre nutrition (gels, barres) à l'entraînement. Votre estomac doit s'habituer à digérer l'effort."
    }
  },
  {
    id: 'run_marathon_fueling',
    sport: 'run',
    title: { en: 'Fueling is training too', fr: 'Le ravito se travaille' },
    body: {
      en: 'Practice carbs in long runs (e.g., 30–60g/h). Your gut adapts like your legs do.',
      fr: "Teste les glucides en sortie longue (ex : 30–60g/h). Ton estomac s’entraîne comme tes jambes."
    },
    match: { keywords: ['marathon', 'semi', 'half', 'long'] }
  },

  // --- RUN (persona flavored) ---
  {
    id: 'run_beginner_runwalk',
    sport: 'run',
    title: { en: 'Run/Walk is a smart strategy', fr: 'Marche/course : stratégie intelligente' },
    body: {
      en: 'Alternating run/walk keeps injury risk low and builds aerobic base fast—perfect for a safe return.',
      fr: "Alterner course/marche limite le risque de blessure et construit vite le foncier—idéal pour reprendre en sécurité."
    },
    match: { personas: ['beginner_fitness'], keywords: ['getting back', 'reprise', '5 km', '5k'] }
  },
  {
    id: 'run_performance_paces',
    sport: 'run',
    title: { en: 'Training paces evolve—don’t lock them', fr: "Les allures évoluent : ne les fige pas" },
    body: {
      en: 'We’ll adjust targets as you execute. A slightly slower target done consistently beats a faster target skipped.',
      fr: "On ajustera les cibles selon ton exécution. Une cible un peu plus lente tenue régulièrement vaut mieux qu’une cible trop ambitieuse."
    },
    match: { personas: ['performance_advanced', 'recreational_5k_10k'], keywords: ['chrono', 'time', 'improve'] }
  },

  // --- BIKE (generic) ---
  {
    id: 'bike_endurance_foundation',
    sport: 'bike',
    title: { en: 'Endurance rides build the engine', fr: 'L’endurance construit le moteur' },
    body: {
      en: 'Most cycling fitness comes from steady Z2. Keep it controlled and you’ll absorb the hard sessions better.',
      fr: "La majorité du progrès vient de la Z2. Une endurance maîtrisée aide à mieux encaisser les séances dures."
    }
  },
  {
    id: 'bike_sweet_spot',
    sport: 'bike',
    title: { en: 'Sweet spot: efficient, but dose it', fr: 'Sweet spot : efficace, mais à doser' },
    body: {
      en: 'Sweet spot works well, but it’s not “easy”. We’ll place it with enough recovery so you progress weekly.',
      fr: "Le sweet spot marche très bien, mais ce n’est pas “facile”. On le place avec assez de récup’ pour progresser semaine après semaine."
    },
    match: { personas: ['bike_gran_fondo', 'bike_time_trial_ftp', 'bike_fitness_general'], keywords: ['ftp', 'chrono', 'time trial'] }
  },
  {
    id: 'bike_climbing_pacing',
    sport: 'bike',
    title: { en: 'Climbing: pace the first half', fr: 'Montées : gérer la 1ʳᵉ moitié' },
    body: {
      en: 'Start 5–10% easier than you think. If you can still push near the top, you paced it right.',
      fr: "Pars 5–10% plus facile que prévu. Si tu peux encore pousser vers le sommet, c’était le bon pacing."
    },
    match: { personas: ['bike_mountain_climbing'], keywords: ['mountain', 'montagne', 'climb', 'col'] }
  },
  {
    id: 'bike_position_matters',
    sport: 'bike',
    title: { en: 'Position matters (and saves watts)', fr: 'La position compte (et économise des watts)' },
    body: {
      en: 'For TT/fast rides, a stable position is free speed. Even small tweaks can outperform extra fitness.',
      fr: "Sur contre-la-montre / sorties rapides, une position stable = vitesse gratuite. Un petit réglage peut valoir plus que du fitness."
    },
    match: { personas: ['bike_time_trial_ftp'], keywords: ['tt', 'time trial', 'chrono'] }
  },

  // --- TRIATHLON ---
  {
    id: 'tri_brick_intro',
    sport: 'triathlon',
    title: { en: 'Brick sessions: start short', fr: 'Enchaînements : commencez court' },
    body: {
      en: 'Running off the bike feels weird ("jelly legs"). Start with just 10-15 min run after biking to adapt neuromuscularly.',
      fr: "Courir après le vélo donne des jambes molles. Commencez par seulement 10-15 min de course après le vélo pour habituer les muscles."
    }
  },
  {
    id: 'tri_open_water',
    sport: 'triathlon',
    title: { en: 'Pool vs Open Water', fr: 'Piscine vs Eau Libre' },
    body: {
      en: 'Pool swimming builds fitness, but open water builds confidence. Sighting (looking up) is a skill to practice.',
      fr: "La piscine construit le physique, l'eau libre la confiance. Le repérage (lever la tête) est une technique à part entière."
    }
  },
  {
    id: 'tri_transitions',
    sport: 'triathlon',
    title: { en: 'Transitions are free time', fr: 'Les transitions, c’est du temps gratuit' },
    body: {
      en: 'You can save minutes just by organizing your transition area properly. Practice T1 and T2 mentally.',
      fr: "Vous pouvez gagner des minutes juste en organisant bien votre zone. Répétez T1 et T2 mentalement."
    }
  }
];

const matchesTip = ({ tip, sport, personaId, text }) => {
  if (tip.sport === 'any') return true;
  if (sport === 'triathlon' && (tip.sport === 'run' || tip.sport === 'bike')) return true;
  if (tip.sport !== sport) return false;

  const personas = tip.match?.personas || [];
  if (personas.length > 0 && !personas.includes(personaId)) return false;

  const keywords = (tip.match?.keywords || []).map(normalize).filter(Boolean);
  if (keywords.length === 0) return true;
  return keywords.some((k) => text.includes(k));
};

export function selectTips({ sport = 'run', personaId = '', goalPrimary = '', eventDistanceType = '' }) {
  const s = sport; // Keep raw sport (run, bike, triathlon)
  const p = String(personaId || '');
  const text = `${normalize(goalPrimary)} ${normalize(eventDistanceType)}`.trim();

  const matched = TIPS.filter((tip) => matchesTip({ tip, sport: s, personaId: p, text }));
  if (matched.length >= 4) return matched;

  // Fallbacks
  return TIPS.filter((tip) => matchesTip({ tip, sport: s, personaId: '', text: '' }));
}


